import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '@/utils/AxiosInstance';
import { RootState } from '@/shuttle/store/store';
import { AxiosError } from 'axios';
import { PaymentType } from '@/projects/shuttle/constants';

// Types
export interface AddressInfo {
  name: string;
  arrival_time: string;
}

export interface DriverInfo {
  name: string;
  photo_url: string;
  car_name: string;
  car_number_plate: string;
  phone: string;
}

export interface TariffOption {
  kind: string;
  name: string;
  price: number;
  price_per_ride: number;
  ride_count: number;
  start_date: string;
  end_date: string;
  message: string;
  payment_type: PaymentType;
  rides_text: string;
  dates_text: string;
}

export interface DepositInfo {
  price: number;
}

export interface TariffInfo {
  pickup_address: AddressInfo;
  dropoff_address: AddressInfo;
  driver: DriverInfo;
  tariff_options: TariffOption[];
  deposit: DepositInfo | null;
}

interface TariffState {
  isLoading: boolean;
  error: string | null;
  tariffInfo: TariffInfo | null;
  selectedOption: TariffOption | null;
}

// Initial state
const initialState: TariffState = {
  isLoading: false,
  error: null,
  tariffInfo: null,
  selectedOption: null,
};

// Async thunk for fetching tariff info
export interface FetchTariffInfoParams {
  tourId: string;
  pickup_stop_id?: string | number;
  dropoff_stop_id?: string | number;
  phone?: string;
}



// Define the return type explicitly
export const fetchTariffInfo = createAsyncThunk<TariffInfo, FetchTariffInfoParams>(
  'tariff/fetchTariffInfo',
  async ({ tourId, pickup_stop_id, dropoff_stop_id, phone }: FetchTariffInfoParams, { rejectWithValue }) => {
    try {
      let url = `/tariff-info/${tourId}`;
      const params = new URLSearchParams();
      
      if (pickup_stop_id) {
        params.append('pickup_bs_id', pickup_stop_id.toString());
      }
      
      if (dropoff_stop_id) {
        params.append('dropoff_bs_id', dropoff_stop_id.toString());
      }

      if (phone) {
        params.append('phone', phone);
      }

      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      return await axiosInstance.get(url);
    } catch (error) {
      const axiosError = error as AxiosError;
      return rejectWithValue(
        axiosError.response ? (axiosError.response.data as { message: string }).message : 'Failed to fetch tariff information'
      );
    }
  }
);

// Slice
const tariffSlice = createSlice({
  name: 'tariff',
  initialState,
  reducers: {
    setSelectedOption: (state, action) => {
      state.selectedOption = action.payload;
    },
    resetTariffInfo: (state) => {
      state.tariffInfo = null;
      state.selectedOption = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTariffInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.tariffInfo = null;
        state.selectedOption = null;
      })
      .addCase(fetchTariffInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tariffInfo = action.payload;
      })
      .addCase(fetchTariffInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Actions
export const { setSelectedOption, resetTariffInfo } = tariffSlice.actions;

// Selectors
export const tariffSelector = (state: RootState) => state.tariffReducer;

export default tariffSlice.reducer; 